<template>
  <div class="layer">
    <div class="inner">
      <button type="button" class="btnClose" @click="close"></button>
      <h2 class="layerTitle">{{categoryName[defaultLang] || categoryName}} &#8250; {{collectionItemName[defaultLang] || ''}}</h2>

      <div class="innerTitle mb15">
        <div class="checkbox">
          <input type="checkbox" id="item_activate" v-model="isActive">
          <label for="item_activate">{{ locale.activate }}</label>
        </div>
      </div>

      <div class="flexBox info mb15">
        <div class="flexInner">
          <div class="label groupLabel mb15">{{ locale.basic_info }}</div>
          <div class="mb15">
            <div class="innerTitle setLabel">{{locale.image}}</div>
            <div class="imgList">
              <div v-if="imageList.length > 0"
                class="imgItem"
                :style="{backgroundImage: `url(${imageList[0]})`}">
                <button type="button" class="btnDel" @click="delImg(0)"></button>
              </div>
              <label v-else class="imgItem addImg">
                <input type="file" @change="uploadImage($event)">
              </label>
            </div>
          </div>
          <div class="mb15">
            <div class="innerTitle setLabel">{{locale.name}}</div>
            <SetLanguageValue
              :locale="locale"
              :langValue="collectionItemName"
              :textarea="true" />
          </div>
          <div class="mb15">
            <div class="innerTitle setLabel">{{locale.descTop}}</div>
            <SetLanguageValue
              :locale="locale"
              :langValue="descTop"
              :textarea="true" />
          </div>
          <div class="mb15">
            <div class="innerTitle setLabel">{{ locale.descBottom }}</div>
            <SetLanguageValue
              :locale="locale"
              :langValue="descBottom"
              :textarea="true" />
          </div>
        </div>
        <div class="flexInner" data-flex="2">
          <div class="label groupLabel mb15">그룹아이템 관리</div>
          <div v-if="collectionClasses?.length > 0">
            <div class="collectionClasses mb10">
              <button
                v-for="item in collectionClasses"
                :key="item.collectionClassId"
                type="button"
                :class="{btnWhite: item.collectionClassId !== activeClass}"
                class="btn sizeS"
                @click="activeClass = item.collectionClassId">
                {{ item.collectionClassName[defaultLang] }}
                {{ `(${getSubItemCount(item.collectionClassId)})` }}
              </button>
              <button class="btn sizeS btnWhite" @click="$emit('setClasses', true)">분류 관리</button>
            </div>

            <div class="addItems mb10">
              <button
                type="button"
                class="btnAdd"
                @click="subItemDetail = 'new'">
                추가하기
              </button>
              <button
                type="button"
                class="btnSearch"
                @click="openSubItemAllLayer">
                아이템 찾기
              </button>
            </div>
            <p class="selectRequired">선택방식 <span :class="{stkr: true, redStkr: isRequiredClass}">{{ isRequiredClass ? '필수' : '선택' }}</span></p>
            <div class="subItemsByClass">
              <draggable
                v-model="activeSubItemIds"
                class="subItemDraggable">
                <div
                  v-for="item in subItemsByClass"
                  :key="item.subItemId"
                  class="subItem"
                  @click="subItemDetail = item.subItemId">
                  <div class="thumb" :style="{backgroundImage: `url(${item.attribute.thumb})`}"></div>
                  <div class="name">{{ item.collectionItemName[defaultLang] }}</div>
                  <button type="button" class="btnDel" @click="delSubItem(item.subItemId)"></button>
                </div>
              </draggable>
            </div>
          </div>
          <div v-else class="noData" @click="$emit('setClasses', true)">아이템 분류를 먼저 설정해주세요.</div>
        </div>
      </div>
      <div class="btns">
        <button v-if="collectionItemId !== 'new'" type="button" class="btn btnRed btnLeft btnAuto" @click="delItem">{{ locale.delete }}</button>
        <button type="button" class="btn btnWhite" @click="close">{{ locale.cancel }}</button>
        <button type="button" class="btn btnPrimary" @click.stop="save">{{locale.save}}</button>
      </div>
    </div>

    <div v-if="subItemAllLayer" class="layer subItemAllLayer">
      <div class="inner">
        <button type="button" class="btnClose" @click="subItemAllLayer = false"></button>
        <h2 class="layerTitle">아이템</h2>
        <input type="text" class="input searchInput" v-model="search" />
        <div v-if="subItemsAll.length === 0" class="noData">등록된 아이템이 없습니다.</div>
        <div v-else class="subItemAll">
          <div
            v-for="item in filteredSubItemsAll"
            :key="item.subItemId"
            class="subItem"
            :class="{isChecked: checkedSubItems.includes(item.subItemId)}"
            @click="checkSubItemId(item.subItemId)">
            <div class="thumb" :style="{backgroundImage: `url(${item.attribute.thumb})`}"></div>
            <div class="name">{{ item.collectionItemName[defaultLang] }}</div>
          </div>
        </div>
        <div class="btns">
          <button type="button" class="btn btnWhite" @click="subItemAllLayer = false">{{ locale.cancel }}</button>
          <button type="button" class="btn btnPrimary" :disabled="subItemsAll.length === 0" @click="setSubItem">{{ locale.apply }}</button>
        </div>
      </div>
    </div>
    
    <Detail
      v-if="subItemDetail"
      :locale="locale"
      :collectionItemId="subItemDetail"
      :categoryId="categoryId"
      :defaultLang="defaultLang"
      :isSubItem="true"
      @completeSave="saveSubItem"
      @completeDelete="deleteSubItem"
      @close="subItemDetail = null" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import LANG from '@/common/lang'
import UNIQUE from '@/common/unique'
import regExp from '@/common/regExp'
import draggable from 'vuedraggable'
import ColorPicker from '@/components/Util/ColorPicker'
import SetLanguageValue from '@/components/Util/SetLanguageValue'
import InputRange from '@/components/Util/InputRange'
import Category from './Category'
import Detail from './Detail'

export default {
  name: 'GroupItemDetail',
  props: [
    'locale',
    'collectionItemId',
    'categoryId',
    'defaultLang',
    'collectionClasses'
  ],
  components: {
    draggable,
    ColorPicker,
    SetLanguageValue,
    InputRange,
    Category,
    Detail
  },
  computed: {
    pageId () {
      return this.$route.params.pageId
    },
    categoryName () {
      return this.$store.getters['collection/getCategoryNameById'](this.categoryId) || this.categoryId
    },
    filteredList () {
      const word = regExp.replaceAllEmpty(this.searchItem)
      const list = this.list
      return list.filter(x => {
        const shopPageName = regExp.replaceAllEmpty(x.pageName || '')
        const nameValues = Object.values(x.shopItemName).map(value => regExp.replaceAllEmpty(value))
        const pId = regExp.replaceAllEmpty(x.pId)
        return word.length > 0
          ? nameValues.find(name => regExp.replaceAllEmpty(name).includes(word)) || pId.includes(word) || shopPageName.includes(word)
          : true
      })
    },
    activeClassKey () {
      return this.activeClass || 'unknown'
    },
    isRequiredClass () {
      const classKey = this.activeClassKey
      const findClass = this.collectionClasses && this.collectionClasses.find(collectionClass => collectionClass.collectionClassId === classKey)
      return findClass && findClass.isRequired
    },
    filteredSubItemsAll () {
      const list = this.subItemsAll
      const word = regExp.replaceAllEmpty(this.search)
      if (word.length === 0) return list
      return list.filter(item => {
        const nameValues = Object.values(item.collectionItemName).map(value => regExp.replaceAllEmpty(value))
        return nameValues.find(name => regExp.replaceAllEmpty(name).includes(word))
      })
    },
    activeSubItemIds: {
      get () {
        const classId = this.activeClassKey
        return this.itemTypeSetting[classId] || []
      },
      set (value) {
        const classId = this.activeClassKey

        this.$set(
          this.itemTypeSetting,
          classId,
          value
        )
      }
    },
    subItemsByClass () {
      const subItemIds = this.activeSubItemIds
      const subItemsAll = this.subItemsAll

      return subItemIds
        .map(subItemId => {
          return subItemsAll.find(item => item.subItemId === subItemId)
        })
        .filter(Boolean)
    }
  },
  created () {
    this.init()
  },
  mounted () {
  },
  data () {
    return {
      isActive: true,
      collectionItemName: LANG.valueObj(''),
      descTop: LANG.valueObj(''),
      descBottom: LANG.valueObj(''),
      category: [],
      imageList: [],
      itemType: 'group',
      itemTypeSetting: {},
      activeClass: '',
      subItemAllLayer: false,
      subItemsAll: [],
      checkedSubItems: [],
      search: '',
      subItemDetail: null
    }
  },
  methods: {
    async init () {
      if (this.collectionItemId === 'new' && this.categoryId) {
        this.category = [this.categoryId]
      }
      if (this.collectionItemId !== 'new') {
        await this.getCollectionItem()
      }
      await this.getSubItems()

      this.normalizeItemTypeSetting()
      if (this.collectionClasses?.length > 0) {
        this.activeClass = this.collectionClasses[0].collectionClassId
      }
    },
    normalizeItemTypeSetting () {
      const validSubItemIdSet = new Set(
        this.subItemsAll.map(item => item.subItemId)
      )

      const hasCollectionClasses = this.collectionClasses?.length > 0

      const validClassIdSet = new Set(
        hasCollectionClasses
          ? this.collectionClasses.map(item => item.collectionClassId)
          : ['unknown']
      )

      const next = {}

      Object.keys(this.itemTypeSetting || {}).forEach(classId => {
        if (!validClassIdSet.has(classId)) return

        const subItemIds = this.itemTypeSetting[classId]

        next[classId] = Array.isArray(subItemIds)
          ? subItemIds.filter(subItemId => validSubItemIdSet.has(subItemId))
          : []
      })

      this.itemTypeSetting = next
    },
    getSubItemCount (classId) {
      const key = classId || 'unknown'
      return this.itemTypeSetting[key]?.length || 0
    },
    openSubItemAllLayer () {
      const classId = this.activeClassKey
      this.subItemAllLayer = true
      this.search = ''
      this.checkedSubItems = [
        ...(this.itemTypeSetting[classId] || [])
      ]
      this.getSubItems()
    },
    checkSubItemId (subItemId) {
      this.checkedSubItems = this.checkedSubItems.includes(subItemId)
        ? this.checkedSubItems.filter(id => id !== subItemId)
        : [...this.checkedSubItems, subItemId]
    },
    setSubItem () {
      const classId = this.activeClassKey

      this.$set(
        this.itemTypeSetting,
        classId,
        [...this.checkedSubItems]
      )
      this.search = ''
      this.checkedSubItems = []
      this.subItemAllLayer = false
    },
    async addSubItem (subItemId) {
      const classId = this.activeClassKey
      const items = this.itemTypeSetting[classId] || []
      this.$set(
        this.itemTypeSetting,
        classId,
        items.includes(subItemId)
          ? items
          : [...items, subItemId]
      )
      await this.getSubItems()
      this.normalizeItemTypeSetting()
    },
    delSubItem (subItemId) {
      const classId = this.activeClassKey
      const items = this.itemTypeSetting[classId] || []

      this.$set(
        this.itemTypeSetting,
        classId,
        items.filter(id => id !== subItemId)
      )
    },
    async getSubItems () {
      const data = {
        pageId: this.pageId,
        categoryId: this.categoryId
      }

      this.$store.commit('onLoading', true)
      const res = await this.$store.dispatch('collection/getCollectionSubItemList', data)
      const list = res?.message?.subItems
      if (list) {
        this.subItemsAll = list
      } else {
        alert(this.locale.system_error)
      }
      this.$store.commit('onLoading', false)
    },
    // (그룹)아이템 호출
    async getCollectionItem () {
      const data = {
        pageId: this.pageId,
        collectionItemId: this.collectionItemId
      }
      this.$store.commit('onLoading', true)
      const res = await this.$store.dispatch('collection/getCollectionItem', data)
      const detail = res?.message
      if (detail) {
        this.isActive = detail.isActive !== false
        this.collectionItemName = detail.collectionItemName
        this.category = detail.category?.map(x => x.categoryId) || [this.categoryId]
        this.descTop = detail.attribute.descTop
        this.descBottom = detail.attribute.descBottom
        this.imageList = detail.attribute.imageList || []
        this.itemType = detail.type || 'group'
        this.itemTypeSetting = detail.typeSetting || {}
      } else {
        alert(this.locale.system_error)
      }
      this.$store.commit('onLoading', false)
    },
    uploadImage (e) {
      const data = {
        id: UNIQUE.mid(),
        file: e.target.files[0]
      }
      const isImage = regExp.image.test(data.file.name.slice(data.file.name.lastIndexOf('.') + 1).toLowerCase())
      if (isImage) {
        this.$store.dispatch('util/uploadFile', data).then((res) => {
          this.imageList.push(res.source)
          e.target.value = ''
        })
      } else {
        alert(this.locale.validate_upload_image)
      }
    },
    delImg (index) {
      this.imageList = this.imageList.filter((x, i) => i !== index)
    },
    async delItem () {
      const data = {
        pageId: this.pageId,
        collectionItemId: this.collectionItemId
      }
      if (window.confirm(this.locale.confirm_delete)) {
        this.$store.commit('onLoading', true)
        const res = await this.$store.dispatch('collection/deleteCollectionItem', data)
        if (res?.status === 200) {
          alert(this.locale.complete_delete)
          this.$emit('completeDelete')
          this.$emit('close')
        }
        this.$store.commit('onLoading', false)
      }
    },
    async save () {
      this.normalizeItemTypeSetting()
      const data = {
        pageId: this.pageId,
        isActive: this.isActive,
        collectionItemId: this.collectionItemId === 'new' ? UNIQUE.uniqueId8() : this.collectionItemId,
        collectionItemName: this.collectionItemName,
        category: this.category,
        attribute: {
          imageList: this.imageList,
          descTop: this.descTop,
          descBottom: this.descBottom,
        },
        type: 'group',
        typeSetting: this.itemTypeSetting || {}
      }
      const func = this.collectionItemId === 'new'
        ? 'collection/createCollectionItem'
        : 'collection/updateCollectionItem'
      if (window.confirm(this.locale.confirm_save)) {
        this.$store.commit('onLoading', true)
        const res = await this.$store.dispatch(func, data)
        if (res?.status === 200) {
          alert(this.locale.complete_save)
          this.$emit('completeSave', data.collectionItemId)
          this.$emit('close')
        }
        this.$store.commit('onLoading', false)
      }
    },
    close () {
      this.$emit('close')
    },
    saveSubItem (subItemId) {
      this.addSubItem(subItemId)
      this.subItemDetail = null
    },
    deleteSubItem (subItemId) {
    }
  }
}
</script>

<style lang="scss" scoped>
.layer {
  .inner {
    max-width: 1100px;
  }
  .info {
    overflow: auto;
  }
}
.imgList {
  .imgItem {
    display: block;
    position: relative;
    width: 160px;
    height: 118px;
    border: 1px solid $color-lgrey2;
    border-radius: 4px;
    overflow: hidden;
    background-color: $color-white;
    background-position: 50% 50%;
    background-size: cover;
    background-repeat: no-repeat;
  }
  .btnDel {
    position: absolute;
    top: 0;
    right: 0;
    width: 30px;
    height: 30px;
    background: $color-white url('~@/assets/img/ico_close_black.svg') 50% 50% / 16px no-repeat;
    opacity: 0.6;
  }
  .addImg {
    border-style: dashed;
    &:after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: url('~@/assets/img/ico_plus.svg') 50% 50% / 16px no-repeat;
      opacity: 0.2;
    }
  }
}
.collectionClasses {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  .btn {
    padding-left: 0.8em;
    padding-right: 0.8em;
  }
}
.subItemAllLayer {
  .inner {
    max-width: 900px;
  }
  .searchInput {
    width: 300px;

  }
  .noData {
    padding-top: 80px;
    padding-bottom: 120px;
    text-align: center;
    font-size: 20px;
    color: $color-lgrey;
  }
}
.subItemAll {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px 8px;
  overflow: auto;
  margin-top: 15px;
  .inner {
    overflow: hidden;
  }
  .subItem {
    color: $color-grey;
  }
  .thumb {
    position: relative;
    height: 0;
    padding-bottom: 74%;
    border: 1px solid $color-lgrey2;
    background-position: 50% 50%;
    background-size: contain;
    background-color: $color-white;
    background-repeat: no-repeat;
  }
  .isChecked {
    color: inherit;
    .thumb:before {
      content: "";
      position: absolute;
      inset: 0;
      border: 4px solid $color-primary;
      background: rgba(0,0,0,0.4);
    }
    .thumb:after {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 24px;
      height: 16px;
      margin-top: -12px;
      margin-left: -12px;
      border-left: 3px solid #fff;
      border-bottom: 3px solid #fff;
      transform: rotate(-45deg);
    }
  }
}
.subItemsByClass {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px 12px;
  .subItemDraggable {
    display: contents;
  }
  .subItem {
    .thumb {
      height: 0;
      padding-bottom: 66.66%;
      background-position: 50% 50%;
      background-size: contain;
      background-repeat: no-repeat;
    }
    .name {
      padding: 4px 8px;
    }
    position: relative;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid $color-lgrey2;
    background-color: #fff;
    .btnDel {
      position: absolute;
      top: 0;
      right: 0;
      width: 30px;
      height: 30px;
      background: rgba(255,255,255,0.8) url('~@/assets/img/ico_close_black.svg') 50% 50% / 12px no-repeat;
      opacity: 0.8;
    }
  }
}
.addItems {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 14px;
  border: none;
  background: none;
  button {
    flex-basis: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    position: relative;
    padding: 8px 12px;
    color: $color-grey;
    border: 1px dashed $color-lgrey2;
    border-radius: 8px;
    background-color: #fff;
    &:before {
      content: "";
      display: block;
      width: 16px;
      height: 16px;
      background-position: 50% 50%;
      background-size: contain;
      background-repeat: no-repeat;
      opacity: 0.2;
    }
  }
  .btnAdd {
    &:before {
      background-image: url('~@/assets/img/ico_plus.svg');
    }
  }
  .btnSearch {
    &:before {
      background-image: url('~@/assets/img/ico_search.svg');
    }
  }
}
.selectRequired {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  color: $color-grey;
  .stkr {
    padding: 4px 6px;
    font-size: 12px;
    line-height: 1;
    color: #fff;
    border-radius: 4px;
    background-color: $color-lgrey;
  }
  .redStkr {
    background-color: $color-red;
  }
}
.noData {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 0;
  text-align: center;
  font-size: 18px;
  color: $color-lgrey;
  border: 1px dashed $color-lgrey2;
  border-radius: 8px;
  background-color: #fff;
  &:before {
    content: "+";
    font-size: 36px;
  }
}
</style>
